import type { SearchScope } from '@/domains/command-center/services/searchService.types'
import type { SearchResultCommand } from '@/domains/command-center/types'

/** Below two characters every scope returns noise, so none of them is asked. */
const MIN_SEARCH_LENGTH = 2

/**
 * Ask every given scope at once, and return their rows in scope order.
 *
 * `allSettled` rather than `all`: the four scopes are four independent endpoints, and one of them
 * failing — a 403 from a permission that changed mid-session, a dropped connection — must cost its
 * own rows and nothing else. `all` would empty the palette because bills timed out.
 *
 * The caller passes only the scopes the user may see, so an unpermitted endpoint is never touched.
 *
 * @param searchTerm - What the user typed or scanned
 * @param scopes - The active, permitted scopes, in the order their groups should appear
 * @returns Every scope's rows, concatenated in scope order. Empty for a term under two characters.
 */
const searchItems = async (searchTerm: string, scopes: SearchScope[]): Promise<SearchResultCommand[]> => {
  if (!searchTerm || searchTerm.length < MIN_SEARCH_LENGTH || scopes.length === 0) {
    return []
  }

  const settled = await Promise.allSettled(scopes.map(scope => scope.search(searchTerm)))

  return settled.flatMap(result => (result.status === 'fulfilled' ? result.value : []))
}

export { searchItems, MIN_SEARCH_LENGTH }
